import { api } from '@lib/api'
import { takeLeading } from '@lib/takeLeading'
import { TaskResponseContent } from '@vetahealth/tuna-can-api'
import dayjs from 'dayjs'
import { shallow } from 'zustand/shallow'
import { createWithEqualityFn } from 'zustand/traditional'
import { useAuthStore } from '../auth'
import { AnsweredTask, TasksState } from './types'

export const initialState: Pick<
  TasksState,
  'taskByRef' | 'task' | 'info' | 'hasTask' | 'answeredTasks' | 'answeredTask'
> = {
  taskByRef: undefined,
  task: undefined,
  info: undefined,
  hasTask: false,
  answeredTasks: undefined,
  answeredTask: undefined,
}

function isAnsweredTask(answeredTask?: TaskResponseContent): answeredTask is AnsweredTask {
  return !!answeredTask?.content && 'assets' in answeredTask.content
}

export const useTasksStore = createWithEqualityFn<TasksState>(
  (set, get) => ({
    ...initialState,
    setTask: ({ task, info }) => set({ task, info }),
    setInfo: (info) => set({ info }),
    getTask: takeLeading(async () => {
      const { user } = useAuthStore.getState()
      const { task } = get()

      if (user?.locale === task?.locale) return

      const activeTask = await api.getActiveTask()

      set({ task: activeTask, hasTask: !!activeTask })
    }),
    getAnsweredTasks: takeLeading(async () => {
      const tasks = await api.getAnsweredTasks()

      set({ answeredTasks: tasks?.sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1)) })
    }),
    getAnsweredTask: takeLeading(async (responseId) => {
      const answeredTask = await api.getAnsweredTask(responseId)

      if (isAnsweredTask(answeredTask)) {
        set({ answeredTask })
      }
    }),
    getTaskByRef: takeLeading(async (ref) => {
      const { user } = useAuthStore.getState()
      const { taskByRef, getTask } = get()

      if (user?.locale === taskByRef?.locale && ref === taskByRef?.ref) return

      const activeTask = await api.getTaskByRef(ref)

      if (activeTask) {
        set({ taskByRef: activeTask, hasTask: true })
      } else {
        if (!get().task) await getTask() // fallback
      }
    }),
    submitTask: async ({ task, response }) => {
      const nextTask = await api.submitTask({
        id: task.id,
        version: task.version || dayjs().toISOString(),
        locale: task.locale || 'en-US',
        followUp: task.followUp,
        response,
      })

      set({
        task: nextTask?.activeTask,
        info: nextTask?.infoScreen,
        taskByRef: undefined,
        hasTask: !!nextTask?.activeTask,
      })
    },
    resetInfo: () => set({ info: undefined }),
    reset: () => set(initialState),
  }),
  shallow,
)
